import { createSelector } from '@reduxjs/toolkit';

import {
  restrictPointSnappingSettingsForWorkLineInsertion,
  WORK_LINE_IGNORED_POINT_SNAPS,
} from '../../library/layout-geometry/work-lines/workLineSnappingSettings';
import { EntitySnapTargetType } from '../../library/layout-snapping/EntitySnapTarget';
import { InteractiveEditSnappingSettings } from '../../library/layout-snapping/interactive-snapping/settings';
import { PointSnappingSettings, PointSnapToggleKey } from '../../library/layout-snapping/point-snapping/settings';
import { EntitySnapTargetSelectionSettings } from '../../library/layout-snapping/settings';
import { RootState } from '../index';
import { PointSnappingContext, SnappingSettingsContext } from '../slices/layoutSlice';
import { getEntitySnapTargetTypesForVisibleObjects } from './lineSnappingSelectors';
import { getPickingObjectInfo, getPickingObjectInfoType, getPointPickingOtherPoint } from './pickingSelectors';
import { getWorkLineInsertionInfo } from './workLineInsertionSelectors';

/**
 * Returns the currently active point snapping context, or null if no snapping is occurring. Unlike the other
 *   `getConfigured` or `getActive` selectors for snapping settings, this selector for the active context returns `null`
 *   when no snapping is occurring.
 */
export const getActiveSnappingContextOrNull = createSelector(
  getPickingObjectInfo,
  (state: RootState) => state.application.inDimensionMode,
  (state: RootState) => state.interactiveEditing.activeEdit,
  (pickingObjectInfo, inDimensionMode, isActiveEditOngoing): SnappingSettingsContext | null => {
    if (
      pickingObjectInfo?.type === 'pickingLayoutPoint' &&
      (pickingObjectInfo.target === 'bearing-envelope-left' || pickingObjectInfo.target === 'bearing-envelope-right')
    ) {
      return 'bearingEnvelope';
    }

    if (
      pickingObjectInfo?.type === 'pickingLayoutPoint' &&
      (pickingObjectInfo.target === 'insertion-envelope-left' ||
        pickingObjectInfo.target === 'insertion-envelope-right' ||
        pickingObjectInfo.target === 'editing-envelope-left' ||
        pickingObjectInfo.target === 'editing-envelope-right')
    ) {
      return 'trussEnvelope';
    }

    if (pickingObjectInfo != null || inDimensionMode) return 'general';

    if (isActiveEditOngoing) return 'interactiveEdit';

    return null;
  },
);

export const getConfiguredPointSnappingContextAndSettings = createSelector(
  getActiveSnappingContextOrNull,
  (state: RootState) => state.layout.pointSnappingSettings,

  (context, pointSnappingSettings): [PointSnappingContext, PointSnappingSettings] =>
    context == null || context === 'interactiveEdit'
      ? ['general', pointSnappingSettings['general']]
      : [context, pointSnappingSettings[context]],
);

function filterSettingsToVisibleTargets<TSettings extends EntitySnapTargetSelectionSettings>(
  settings: TSettings,
  visibleTypes: readonly EntitySnapTargetType[],
): TSettings {
  return {
    ...settings,
    enabledTargetTypes: settings.enabledTargetTypes.filter((target) => visibleTypes.includes(target)),
  };
}

export const getActualPointSnappingContextAndSettings = createSelector(
  getConfiguredPointSnappingContextAndSettings,
  getEntitySnapTargetTypesForVisibleObjects,
  getWorkLineInsertionInfo,
  getPickingObjectInfoType,

  (
    [context, settings],
    visibleTypes,
    workLineInsertionInfo,
    pickingObjectType,
  ): [PointSnappingContext, PointSnappingSettings] => {
    let actualSettings = filterSettingsToVisibleTargets(settings, visibleTypes);

    if (workLineInsertionInfo != null && pickingObjectType !== 'pickingLayoutPoint') {
      actualSettings = restrictPointSnappingSettingsForWorkLineInsertion(actualSettings);
    }

    return [context, actualSettings];
  },
);

export const getActualPointSnappingSettings = createSelector(
  getActualPointSnappingContextAndSettings,
  (contextAndSettings): PointSnappingSettings => contextAndSettings[1],
);

export const getActualInteractiveEditSnappingSettings = createSelector(
  (state: RootState): InteractiveEditSnappingSettings => state.layout.interactiveEditSnappingSettings,
  getEntitySnapTargetTypesForVisibleObjects,
  filterSettingsToVisibleTargets,
);

type SecondPointGatedSnap = 'perpendicularProjection' | 'orthogonal' | 'angle' | 'lengthIncrement';

type DisabledActiveSnaps = Partial<Record<PointSnapToggleKey, string>>;

const NO_DISABLED_ACTIVE_SNAPS = {} satisfies DisabledActiveSnaps;

const WORK_LINE_DISABLED_ACTIVE_SNAPS = {
  nearestPoint: 'Not used when inserting a work line.',
  perpendicularProjection: 'Not used when inserting a work line.',
  orthogonal: 'Not used when inserting a work line.',
  angle: 'Not used when inserting a work line.',
  lengthIncrement: 'Not used when inserting a work line.',
} satisfies Record<(typeof WORK_LINE_IGNORED_POINT_SNAPS)[number], string>;

const SECOND_POINT_DISABLED_ACTIVE_SNAPS = {
  perpendicularProjection: 'Not used for first point.',
  orthogonal: 'Not used for first point.',
  angle: 'Not used for first point.',
  lengthIncrement: 'Not used for first point.',
} satisfies Record<SecondPointGatedSnap, string>;

export const getDisabledActiveSnaps = createSelector(
  getWorkLineInsertionInfo,
  getPickingObjectInfoType,
  getPointPickingOtherPoint,
  (workLineInsertionInfo, pickingObjectType, otherPoint): DisabledActiveSnaps => {
    if (workLineInsertionInfo != null && pickingObjectType !== 'pickingLayoutPoint') {
      return WORK_LINE_DISABLED_ACTIVE_SNAPS;
    }

    if (otherPoint == null) {
      return SECOND_POINT_DISABLED_ACTIVE_SNAPS;
    }

    return NO_DISABLED_ACTIVE_SNAPS;
  },
);
